<template>
    <div class="relative overflow-x-hidden">
        <jet-authentication-card>
            <template #logo>
                <jet-authentication-card-logo />

                <h1
                    class="font-semibold text-iron-900 mt-[26px] text-center text-[26px] tracking-[-0.1px]"
                >
                    {{ $t("auth.register.create_account") }}
                </h1>
                <p class="text-sm font-medium text-iron-500 text-center">
                    {{ $t("auth.register.start_with_bookings") }}
                </p>
            </template>

            <jet-validation-errors class="mb-4" />

            <SSOSignIn
                :register="true"
                :locale="$page.props.locale !== 'cs-CZ' ? 'en-US' : 'cs-CZ'"
            />

            <div class="mb-4 mt-4 relative">
                <div
                    class="inset-0 absolute flex items-center"
                    aria-hidden="true"
                >
                    <div class="border-iron-300 w-full border-t"></div>
                </div>
                <div class="text-sm relative flex justify-center">
                    <span class="px-2 text-iron-500 bg-[#fcfcfc]">
                        {{ $t("auth.sso.or_continue_with") }}
                    </span>
                </div>
            </div>

            <form @submit.prevent="submit" class="mt-6">
                <div>
                    <jet-input
                        id="name"
                        v-model="form.name"
                        type="text"
                        class="block w-full"
                        required
                        autofocus
                        autocomplete="name"
                        :placeholder="$t('auth.register.name')"
                    />
                </div>

                <div class="mt-5">
                    <jet-input
                        id="email"
                        v-model="form.email"
                        type="email"
                        class="block w-full"
                        required
                        :placeholder="$t('auth.register.email')"
                    />
                </div>

                <div class="mt-5">
                    <jet-input
                        id="password"
                        v-model="form.password"
                        type="password"
                        class="block w-full"
                        required
                        autocomplete="new-password"
                        :placeholder="$t('auth.register.password')"
                    />
                </div>

                <div class="mt-5">
                    <jet-input
                        id="password_confirmation"
                        v-model="form.password_confirmation"
                        type="password"
                        class="block w-full"
                        required
                        autocomplete="new-password"
                        :placeholder="$t('auth.register.confirm_password')"
                    />
                </div>

                <div
                    v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature"
                    class="mt-4"
                >
                    <jet-label for="terms">
                        <div class="flex items-center">
                            <jet-checkbox
                                id="terms"
                                v-model:checked="form.terms"
                                name="terms"
                            />

                            <div class="ml-2">
                                {{ $t("auth.register.agree_with") }}
                                <a
                                    target="_blank"
                                    :href="
                                        route('terms.show') +
                                        ($page.props.locale !== 'cs-CZ'
                                            ? ''
                                            : '?locale=cs-CZ')
                                    "
                                    class="text-sm text-iron-600 hover:text-iron-900 underline"
                                    >{{ $t("auth.register.terms") }}</a
                                >
                                {{ $t("and") }}
                                <a
                                    target="_blank"
                                    :href="
                                        route('policy.show') +
                                        ($page.props.locale !== 'cs-CZ'
                                            ? ''
                                            : '?locale=cs-CZ')
                                    "
                                    class="text-sm text-iron-600 hover:text-iron-900 underline"
                                    >{{ $t("auth.register.privacy") }}</a
                                >
                            </div>
                        </div>
                    </jet-label>
                </div>

                <!-- Turnstile widget (hidden until executed) -->
                <Turnstile
                    ref="turnstileRef"
                    size="normal"
                    appearance="execute"
                    execution="execute"
                    @token="handleTurnstileToken"
                />

                <div class="mt-10 flex items-center justify-end">
                    <ThemedButton class="w-full" :disabled="isSubmitting">
                        {{ $t("auth.register.register") }}
                    </ThemedButton>
                </div>
            </form>

            <template #under>
                <p class="mt-8 text-sm text-iron-500 text-center">
                    {{ $t("auth.register.already_registered") }}
                    <!-- space -->
                    <a
                        :href="
                            $page.props.locale !== 'cs-CZ'
                                ? '/login'
                                : '/login?locale=cs-CZ'
                        "
                        class="font-semibold leading-6 text-z-primary-600 hover:opacity-75"
                        >{{ $t("auth.register.sign_in_here") }}</a
                    >
                </p>

                <LanguageSwitcher />
            </template>
        </jet-authentication-card>
    </div>
</template>

<script setup lang="ts">
import JetAuthenticationCard from "../../Jetstream/AuthenticationCard.vue";
import JetAuthenticationCardLogo from "../../Jetstream/AuthenticationCardLogo.vue";
import JetInput from "../../Jetstream/Input.vue";
import JetCheckbox from "../../Jetstream/Checkbox.vue";
import JetLabel from "../../Jetstream/Label.vue";
import JetValidationErrors from "../../Jetstream/ValidationErrors.vue";
import SSOSignIn from "./components/SSOSignIn.vue";
import LanguageSwitcher from "./components/LanguageSwitcher.vue";
import Turnstile from "../../components/Turnstile.vue";
import { router, useForm, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useUrlSearchParams } from "@vueuse/core";
import ThemedButton from "../Zaptime/App/Atomic/ThemedButton.vue";
import { useAffiliate } from "@/composables/useAffiliate";
import { useAnalytics } from "@/composables/useAnalytics";
import { onMounted, ref } from "vue";

const params = useUrlSearchParams("history");
const page = usePage();
const analytics = useAnalytics();
const turnstileRef = ref<InstanceType<typeof Turnstile> | null>(null);
const isSubmitting = ref(false);

const form = useForm({
    name: "",
    email: typeof params.email === "string" ? params.email : "",
    password: "",
    password_confirmation: "",
    country: "CZ",
    terms: true,
    locale: params.locale !== "cs-CZ" ? "en-US" : "cs-CZ",
    turnstileToken: "",
});

useAffiliate();

onMounted(() => {
    // Track form start
    analytics.trackFormStart("register_form");
});

function handleTurnstileToken(token: string) {
    form.turnstileToken = token;
    // Submit form after receiving token
    submitForm();
}

function submit() {
    if (isSubmitting.value) {
        return;
    }

    // Execute Turnstile challenge
    if (turnstileRef.value) {
        isSubmitting.value = true;
        turnstileRef.value.execute();
    }
}

function submitForm() {
    // Track form completion attempt
    analytics.trackFormComplete("register_form", {
        email: form.email,
        country: form.country,
        locale: form.locale,
    });

    form.post(route("register"), {
        onFinish: () => {
            form.reset("password", "password_confirmation");
            isSubmitting.value = false;
            // Reset Turnstile on error
            if (
                turnstileRef.value &&
                form.errors &&
                Object.keys(form.errors).length > 0
            ) {
                turnstileRef.value.reset();
                form.turnstileToken = "";
            }
        },
        onSuccess: () => {
            const userId = page.props.userId as string | null;
            const user = page.props.user as Record<string, any> | null;
            if (userId && user) {
                analytics.identifyUser(String(userId), {
                    email: user.email,
                    name: user.name,
                });
            }

            // Track successful registration conversion
            analytics.trackSignup({
                email: form.email,
                name: form.name,
                country: form.country,
                locale: form.locale,
                user_id: userId ? String(userId) : undefined,
            });

            router.visit(route("verification.notice"));
        },
        onError: () => {
            // Reset Turnstile on validation errors
            if (turnstileRef.value) {
                turnstileRef.value.reset();
                form.turnstileToken = "";
            }
            isSubmitting.value = false;
        },
    });
}
</script>
